import { unzipSync, zipSync } from 'fflate';
import { decodePaintTree, encodePaintTree } from './paintCodec';
import type { EditorDocument, PaintNode } from './types';

interface Modification { paint: string | null; original: string }

export async function export3MF(document: EditorDocument): Promise<Uint8Array> {
  if (!document.dirty) return document.originalBytes;
  const entries = unzipSync(document.originalBytes);
  const byPath = new Map<string, Map<number, Modification>>();
  for (const part of document.parts) {
    let modifications = byPath.get(part.modelPath);
    if (!modifications) { modifications = new Map(); byPath.set(part.modelPath, modifications); }
    for (const triangle of part.triangles) {
      const paint = paintAttribute(triangle.tree);
      const normalizedOriginal = triangle.originalPaint.toUpperCase();
      if ((paint ?? '') === normalizedOriginal) continue;
      modifications.set(triangle.modelTriangleIndex, { paint, original: triangle.originalPaint });
    }
  }
  for (const [path, modifications] of byPath) {
    if (!modifications.size) continue;
    const entry = entries[path];
    if (!entry) throw new Error(`Cannot export: source model entry ${path} is missing`);
    const xml = new TextDecoder().decode(entry);
    const patched = patchTrianglePaint(xml, modifications);
    entries[path] = new TextEncoder().encode(patched);
  }
  const output = zipSync(entries, { level: 6 });
  validateExport(output, byPath);
  return output;
}

function paintAttribute(tree: PaintNode): string | null {
  if (!tree.children && tree.state === 0) return null;
  return encodePaintTree(tree);
}

export function patchTrianglePaint(xml: string, modifications: Map<number, Modification>): string {
  let triangleIndex = -1;
  return xml.replace(/<(?:[\w.-]+:)?triangle\b[^>]*>/gi, (tag) => {
    triangleIndex += 1;
    const modification = modifications.get(triangleIndex);
    if (!modification) return tag;
    const attribute = /\s(?:[\w.-]+:)?paint_color\s*=\s*(["'])[^"']*\1/i;
    if (modification.paint === null) return tag.replace(attribute, '');
    if (attribute.test(tag)) return tag.replace(attribute, ` paint_color="${modification.paint}"`);
    return tag.replace(/\s*\/?\s*>$/, (ending) => ` paint_color="${modification.paint}"${ending.includes('/') ? '/>' : '>'}`);
  });
}

function validateExport(output: Uint8Array, expected: Map<string, Map<number, Modification>>): void {
  const entries = unzipSync(output);
  if (!entries['3D/3dmodel.model']) throw new Error('Export validation failed: root model missing');
  for (const [path, modifications] of expected) {
    const entry = entries[path];
    if (!entry) throw new Error(`Export validation failed: ${path} missing`);
    const xml = new TextDecoder().decode(entry);
    let index = -1; let checked = 0;
    xml.replace(/<(?:[\w.-]+:)?triangle\b[^>]*>/gi, (tag) => {
      index += 1;
      const expectedValue = modifications.get(index);
      if (!expectedValue) return tag;
      const match = tag.match(/\s(?:[\w.-]+:)?paint_color\s*=\s*["']([^"']*)["']/i);
      const actual = match?.[1] ?? null;
      if (actual !== expectedValue.paint) throw new Error(`Export validation failed at ${path} triangle ${index}`);
      if (actual) decodePaintTree(actual);
      checked += 1;
      return tag;
    });
    if (checked !== modifications.size) throw new Error(`Export validation failed: triangle count mismatch in ${path}`);
  }
}

export function outputFileName(fileName: string): string {
  return `${fileName.replace(/\.3mf$/i, '')}_painted.3mf`;
}
